import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { useAuth } from './AuthContext';
import { useSocket } from './SocketContext';
import { messagingAPI } from '../services/api';
import { playMessageNotificationSound } from '../utils/messageSound';
import { messagingFocusRef } from '../utils/messagingFocusRef';

const MessagingUnreadContext = createContext({
  totalUnread: 0,
  refreshUnread: async () => {},
  syncUnreadFromConversations: () => {}
});

function sumUnread(conversations) {
  return (conversations || []).reduce((sum, c) => sum + (Number(c.unread) || 0), 0);
}

export function MessagingUnreadProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const { subscribeMessages } = useSocket();
  const [totalUnread, setTotalUnread] = useState(0);
  const debounceRef = useRef(null);

  const syncUnreadFromConversations = useCallback((conversations) => {
    setTotalUnread(sumUnread(conversations));
  }, []);

  const refreshUnread = useCallback(async () => {
    if (!isAuthenticated) {
      setTotalUnread(0);
      return;
    }
    try {
      const { data } = await messagingAPI.listConversations();
      setTotalUnread(sumUnread(data.conversations));
    } catch {
      /* ignore */
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setTotalUnread(0);
      return undefined;
    }
    refreshUnread();
    return undefined;
  }, [isAuthenticated, refreshUnread]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    return subscribeMessages((payload) => {
      const { conversationId, message: msg } = payload;
      if (!msg || !conversationId) return;

      const viewingThisThread =
        messagingFocusRef.messagingTabActive &&
        String(messagingFocusRef.currentConversationId) === String(conversationId);
      if (!viewingThisThread) {
        playMessageNotificationSound();
      }

      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        refreshUnread();
        debounceRef.current = null;
      }, 150);
    });
  }, [isAuthenticated, subscribeMessages, refreshUnread]);

  return (
    <MessagingUnreadContext.Provider
      value={{ totalUnread, refreshUnread, syncUnreadFromConversations }}
    >
      {children}
    </MessagingUnreadContext.Provider>
  );
}

export function useMessagingUnread() {
  return useContext(MessagingUnreadContext);
}
